import { motion } from "framer-motion";
import { ArrowRight, Phone, CheckCircle } from "lucide-react";
import { staggerContainer, fadeUp, fadeRight } from "../animations/variants";
import logoImg from "../assests/indur_logo.png.png";
import ctMachineImg from "../assests/ct_scan_machine.png";

const CORAL = "#e8523a";

const facilities = [
  "1.5 Tesla Hdxt",
  "GE Brivo 32",
  "Whole Body Imaging",
  "Brain & Neuro Imaging",
  "MR Angiography",
  "MRCP — Pancreato-Cholangiography",
  "Abdomen & Chest CT/MRI",
  "MR Urography",
  "24 / 7 Emergency Service",
];

export default function HeroSection() {
  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0c2340 100%)",
      }}
    >
      {/* ── Background elements ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large glowing orbs */}
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.12, 0.22, 0.12] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${CORAL}55, transparent 70%)`,
          }}
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.08, 0.18, 0.08] }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 3,
          }}
          className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full"
          style={{
            background: `radial-gradient(circle, #38bdf855, transparent 70%)`,
          }}
        />
        {/* Fine dot-grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-20 pb-12 sm:pt-28 sm:pb-16 lg:pt-32">
        <div className="grid lg:grid-cols-[1fr_480px] gap-14 lg:gap-20 items-start">
          {/* ══════════════ LEFT CONTENT ══════════════ */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {/* Live status pill */}
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2.5 bg-white/8 backdrop-blur border border-white/15 rounded-full px-4 py-2 mb-8"
            >
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              <span className="text-sm font-medium text-green-300">
                Open Now — 24/7 Emergency Scans Available
              </span>
            </motion.div>
            <motion.div
              variants={fadeUp}
              className="flex items-center gap-2 sm:gap-5 -mt-4 sm:-mt-11 mb-8"
            >
              <img
                src={logoImg}
                alt="Indur CT & MRI Centre"
                className="h-28 w-20 sm:h-56 sm:w-40 lg:h-72 lg:w-52 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div
                  className="font-poppins font-black tracking-tight leading-none text-white"
                  style={{ fontSize: "clamp(2rem, 8vw, 5.5rem)" }}
                >
                  INDUR
                </div>
                <div
                  className="font-poppins font-bold uppercase tracking-wide sm:tracking-widest leading-tight"
                  style={{
                    color: CORAL,
                    fontSize: "clamp(0.75rem, 2.8vw, 1.5rem)",
                  }}
                >
                  CT &amp; MRI CENTRE
                </div>
                <div className="text-white/50 text-[10px] sm:text-sm tracking-[0.1em] sm:tracking-[0.18em] uppercase mt-1">
                  Advanced Imaging. Accurate Results.
                </div>
              </div>
            </motion.div>
            {/* Tagline badge */}
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 -mt-1 sm:-mt-7 mb-5 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold"
              style={{
                background:
                  "linear-gradient(90deg, rgba(232,82,58,0.18), rgba(56,189,248,0.18))",
                border: "1px solid rgba(255,255,255,0.18)",
                color: "#fbbf24",
              }}
            >
              <span>⭐</span>
              <span>
                First Center in North Telangana Nizamabad · 30 Years of Trusted
                Services — Trusted by Doctors. Loved by Patients.
              </span>
            </motion.div>
            {/* Headline */}
            <motion.h1
              variants={fadeUp}
              className="font-poppins font-extrabold text-white leading-[1.08] mb-6"
              style={{ fontSize: "clamp(1.4rem, 5vw, 3.2rem)" }}
            >
              North Telangana's{" "}
              <span style={{ color: "#38bdf8" }}>Most Trusted</span>
              <br />
              Diagnostic Imaging Centre
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-white/65 text-sm sm:text-lg leading-relaxed mb-7 sm:mb-9 max-w-lg"
            >
              Precision CT & MRI diagnostics with state-of-the-art 1.5 Tesla
              Hdxt MRI and 32-Slice CT technology — serving Nizamabad and North
              Telangana for{" "}
              <span className="text-white font-semibold">30+ years</span>.
            </motion.p>
            {/* Facility highlights */}
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-10"
            >
              {facilities.map((f) => (
                <div
                  key={f}
                  className="flex items-center gap-2 text-sm text-white/75"
                >
                  <CheckCircle
                    className="w-4 h-4 shrink-0"
                    style={{ color: CORAL }}
                  />
                  {f}
                </div>
              ))}
            </motion.div>
            {/* CTA buttons */}
            <motion.div
              variants={fadeUp}
              className="flex flex-wrap gap-4 mb-10"
            >
              <motion.button
                onClick={() => scrollTo("appointment")}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 font-bold px-8 py-4 rounded-full shadow-2xl transition-all duration-300 text-white"
                style={{
                  background: `linear-gradient(135deg, ${CORAL}, #c0392b)`,
                  boxShadow: `0 8px 32px ${CORAL}55`,
                }}
              >
                Book Appointment <ArrowRight className="w-4 h-4" />
              </motion.button>
              <motion.a
                href="tel:+916301195478"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 font-semibold px-8 py-4 rounded-full border-2 border-white/25 text-white hover:bg-white/10 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                +91 63011 95478
              </motion.a>
            </motion.div>
            {/* Trust strip */}
            <motion.div
              variants={fadeUp}
              className="flex flex-wrap items-center gap-4 pt-6 border-t border-white/10"
            >
              {[
                { val: "30+", label: "Years Serving" },
                { val: "3 Lakh+", label: "Scans Done" },
                { val: "99.8%", label: "Accuracy" },
                { val: "24/7", label: "Emergency CT Scan Machine" },
              ].map(({ val, label }) => (
                <div key={label} className="flex flex-col">
                  <span className="font-poppins font-black text-2xl text-white leading-none">
                    {val}
                  </span>
                  <span className="text-white/45 text-xs mt-0.5">{label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* ══════════════ RIGHT VISUAL ══════════════ */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            animate="visible"
            className="relative flex flex-col items-center gap-6 w-full lg:-mt-6"
          >
            {/* CT Machine image */}
            <div
              className="relative w-full rounded-3xl overflow-hidden shadow-2xl"
              style={{ height: "40vh", minHeight: 260 }}
            >
              <img
                src={ctMachineImg}
                alt="CT Scan Machine"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40 rounded-3xl" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="absolute bottom-0 left-0 right-0 leading-none">
        <svg
          viewBox="0 0 1440 90"
          className="w-full"
          preserveAspectRatio="none"
          fill="white"
        >
          <path d="M0,70 C240,90 480,30 720,60 C960,90 1200,30 1440,70 L1440,90 L0,90 Z" />
        </svg>
      </div>
    </section>
  );
}
